import ToolBar from '@/components/ToolBar';
import { useNavigate, useParams } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import { CONTACT_EMAIL_STATUS, SENDAFFIDAVIT } from '@/utils/constant';
import { FormProvider, useForm } from 'react-hook-form';
import { Download, Ellipsis, SquareArrowUpRight } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useContext, useEffect, useState } from 'react';
import { toast } from '@/components/ui/use-toast';
import { getClientContactsById } from '@/services/Network';

import {
  ContactGetAllParams,
  ContactResponse,
  ContactStatusUpdateRequestEmailStatus,
  useClientClientIdGet,
  useContactContactIdStatusPut,
  UserResponseSchemaRoleId,
} from '@brainvaulttech/dgr-react-sdk';
import { useUserStore } from '@/stores/user';
import { DataTable } from '@/components/DataTable';
import { capitalizeFirstLetter, formatPhoneNumber } from '@/lib/utils';
import { NavigationBlockerContext } from '@/context/NavigationBlockerContext';
import { OneLineText } from '@/components/OneLineText';
import Loader from '@/components/Loader';
import { NotFoundRedirectionWrapper } from '@/components/NotFoundRedirectWrapper';
import { Button } from '@/components/ui/button';
import { ExportClientContactsDialog } from './ExportClientContactsDialog';

export const UpdateClientContact = () => {
  const navigate = useNavigate();
  const methods = useForm();
  const { clientId } = useParams<string>();
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const isAdmin = useUserStore((state) => state).user?.roleId === UserResponseSchemaRoleId.ADMIN;
  const { setIsDirty } = useContext(NavigationBlockerContext);
  const { data: clientData, isLoading, isError } = useClientClientIdGet(clientId!);
  const { mutate: changeEmailStatus } = useContactContactIdStatusPut();
  const { mutate: changeAffidavitStatus } = useContactContactIdStatusPut();

  const sendAffidavitHandler = (
    contactId: string,
    email: string,
    sendAffidavit: boolean,
    emailStatus: 'EACH' | 'COMPLETED' | 'NONE'
  ) => {
    changeAffidavitStatus(
      { id: contactId, data: { sendAffidavit, email, emailStatus } },
      {
        onSuccess: () => {
          toast({
            variant: 'default',
            description: `Send Affidavit has been updated.`,
            duration: 3000,
          });
        },
        onError: () => {
          toast({
            variant: 'destructive',
            description: 'Failed to update',
            duration: 3000,
          });
        },
      }
    );
  };

  const emailStatusHandler = (contactId: string, email: string, status: ContactStatusUpdateRequestEmailStatus) => {
    changeEmailStatus(
      { id: contactId, data: { emailStatus: status, email } },
      {
        onSuccess: () => {
          toast({
            variant: 'default',
            description: `Status has been updated.`,
            duration: 3000,
          });
        },
        onError: () => {
          toast({
            variant: 'destructive',
            description: 'Failed to update',
            duration: 3000,
          });
        },
      }
    );
  };

  const columns: ColumnDef<ContactResponse>[] = [
    {
      accessorKey: 'Name',
      header: 'Contact Name',
      cell: ({ cell }) => (
        <OneLineText text={cell.row.original.firstName + ' ' + cell.row.original.lastName} className="min-w-[100px]" />
      ),
      size: 136,
    },
    {
      accessorKey: 'type',
      header: 'Employee Type',

      cell: ({ cell }) => {
        return <div className="min-w-[150px] ">{capitalizeFirstLetter(cell.row.original.type)}</div>;
      },
    },
    {
      accessorKey: 'email',
      header: 'Email Address',
      cell: ({ cell }) => {
        return <OneLineText text={cell.row.original.email} className="min-w-[200px] mr-5" />;
      },
    },
    {
      accessorKey: 'phone',
      header: 'Phone Number/ Ext',
      cell: ({ cell, row }) => {
        return (
          <div className="flex flex-row w-fit min-w-[200px]">
            <span>{cell.getValue<string>() ? cell.getValue<string>() : '-'}</span>
            <span className="px-1">/</span>
            <span className="">{row.original.extension ? row.original.extension : '-'}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'sendAffidavit',
      header: 'Send Affidavit',
      size: 100,
      cell: ({ row }) => {
        const affidavitMails = clientData?.data?.affidavit?.concat(clientData?.data?.affidavitCc ?? []);
        return (
          <div className="min-w-[150px]">
            {isAdmin ? (
              <FormFieldSelect
                name={`sendAffidavit[${row.original.contactId}]`}
                options={SENDAFFIDAVIT}
                className="h-[30px] w-[80px]"
                defaultValue={affidavitMails?.includes(row.original.email) ? 'Yes' : 'No'}
                onChange={(value) => {
                  sendAffidavitHandler(
                    row.original.contactId!,
                    row.original.email,
                    value === 'Yes' ? true : false,
                    row.original.emailStatus!
                  );
                }}
              />
            ) : affidavitMails?.includes(row.original.email) ? (
              'Yes'
            ) : (
              'No'
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'emailStatus',
      header: 'Email Status',
      size: 200,
      cell: ({ cell, row }) => {
        return (
          <div className="min-w-[200px]">
            {isAdmin ? (
              <FormFieldSelect
                name={`email[${row.original.contactId}]`}
                options={CONTACT_EMAIL_STATUS}
                defaultValue={cell.getValue<string>()}
                className="h-[30px] w-[200px] "
                onChange={(value) => {
                  emailStatusHandler(
                    row.original.contactId!,
                    row.original.email,
                    value as ContactStatusUpdateRequestEmailStatus
                  );
                }}
              />
            ) : (
              cell.getValue<string>()
            )}
          </div>
        );
      },
    },

    {
      accessorKey: 'action',
      header: '',
      size: 60,
      cell: ({ row }) => {
        const dropDownData = [
          ...(row.original.fax ? [{ label: 'Fax Number', value: row.original.fax }] : []),
          ...(row.original.lawyerBarId ? [{ label: 'Bar ID', value: row.original.lawyerBarId }] : []),
          ...(row.original.cell ? [{ label: 'Cell Number', value: formatPhoneNumber(row.original.cell) }] : []),
        ];
        return (
          <DropdownMenu>
            <DropdownMenuTrigger className="flex justify-center w-full focus:outline-none focus-visible:outline-none min-w-[70px] ">
              <Ellipsis className="h-6 w-6 cursor-pointer" color="#1551B2" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="mr-[40px] min-w-[256px] w-fit">
              {dropDownData.map((item, index) => (
                <div key={item.label}>
                  <DropdownMenuItem className="pr-[8px] pl-[32px] pt-[6px] pb-[6px] font-medium rounded-none text-sm hover:bg-white cursor-default">
                    {item.label}: {item.value ?? '-'}
                  </DropdownMenuItem>
                  {index !== dropDownData.length - 1 || isAdmin ? <DropdownMenuSeparator className="h-[1px]" /> : null}
                </div>
              ))}
              {isAdmin && (
                <>
                  <DropdownMenuItem
                    className="flex flex-row justify-between pr-[8px] pl-[32px] pt-[6px] pb-[6px] cursor-pointer text-focuscolor font-medium text-sm hover:bg-[#EDF5FF] "
                    onClick={() => {
                      navigate(
                        `/clients/update/${clientId}?stepper=Employees%2F+Contact&contactId=${row.original.contactId}`
                      );
                    }}
                  >
                    <span>Edit Contact</span>
                    <SquareArrowUpRight className="h-4 w-4" color="#1551B2" strokeWidth="1" />
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getContactsByClientId = (params: ContactGetAllParams, options: any) => {
    const param: ContactGetAllParams = {
      ...params,
      clientId: clientId!,
    };
    return getClientContactsById(param, options);
  };

  useEffect(() => {
    setIsDirty(false);
  }, []);
  return (
    <NotFoundRedirectionWrapper isItemNotFound={isError} itemName="client">
      <div className="w-full flex flex-col">
        <Loader isLoading={isLoading}>
          <ToolBar
            title={`${clientData?.data.clientName}'s Contact List`}
            buttonTitle="Add Contact"
            createButton={true}
            handleClick={() => navigate(`/clients/update/${clientId}?stepper=Employees%2F+Contact&contactMode=create`)}
            additionalButton={
              <Button
                variant="outline"
                className="bg-[#FFFFFF] text-primary w-[84px] h-[36px] border-primary hover:bg-[#EDF5FF] p-3"
                onClick={() => setIsExportDialogOpen(true)}
              >
                <div className="flex flex-row items-center gap-2">
                  <Download className="h-4 w-4" />
                  <span className="font-medium hidden sm:block">Export</span>
                </div>
              </Button>
            }
          />
          <FormProvider {...methods}>
            <DataTable columns={columns} apiGet={getContactsByClientId} />
          </FormProvider>
        </Loader>
        {isExportDialogOpen && clientData?.data && (
          <ExportClientContactsDialog onClose={() => setIsExportDialogOpen(false)} clientData={clientData?.data} />
        )}
      </div>
    </NotFoundRedirectionWrapper>
  );
};
